import { useEffect, useRef, useState } from 'react';
import type { TranscriptSegment } from '../api/mediatorService';
import type { Party } from '../types/case';
import { SpeakerBadge } from './SpeakerBadge';

interface Props {
  segments: TranscriptSegment[];
  parties: Party[];
  placeholder?: string;
  /** Names a mislabeled speaker can be reassigned to (phone joins arrive
      as "Call-in user" — Danny, 2026-08-31). Present = badges clickable. */
  relabelOptions?: string[];
  onRelabel?: (fromLabel: string, toLabel: string) => void;
}

const formatTime = (seconds: number): string => {
  const total = Math.max(0, Math.floor(seconds));
  const mm = Math.floor(total / 60)
    .toString()
    .padStart(2, '0');
  const ss = (total % 60).toString().padStart(2, '0');
  return `${mm}:${ss}`;
};

export function TranscriptView({ segments, parties, placeholder, relabelOptions, onRelabel }: Props) {
  const scrollerRef = useRef<HTMLDivElement>(null);
  const atBottomRef = useRef(true);
  const [pickerFor, setPickerFor] = useState<string | null>(null);

  // Track whether the user is scrolled to the bottom; if so, auto-follow new
  // segments. If they scrolled up to read, don't yank them back.
  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const onScroll = () => {
      const slack = 40;
      atBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < slack;
    };
    el.addEventListener('scroll', onScroll);
    return () => el.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    const el = scrollerRef.current;
    if (!el || !atBottomRef.current) return;
    el.scrollTop = el.scrollHeight;
  }, [segments.length]);

  if (segments.length === 0) {
    return (
      <div className="flex h-full items-center justify-center p-6 text-center text-sm text-gray-500">
        {placeholder ?? 'Waiting for speech…'}
      </div>
    );
  }

  return (
    <div ref={scrollerRef} className="h-full overflow-y-auto px-3 py-2">
      <ol className="space-y-3">
        {segments.map((seg) => (
          <li key={seg.id} className="text-sm">
            <div className="flex flex-wrap items-center gap-2">
              {onRelabel && relabelOptions?.length ? (
                <button
                  type="button"
                  className="rounded hover:ring-1 hover:ring-brand"
                  title={`Rename "${seg.speaker_label}" (fixes past and future lines + the Report of Mediation)`}
                  onClick={() => setPickerFor(pickerFor === seg.speaker_label ? null : seg.speaker_label)}
                >
                  <SpeakerBadge
                    speakerLabel={seg.speaker_label}
                    partyId={seg.party_id}
                    parties={parties}
                  />
                </button>
              ) : (
                <SpeakerBadge
                  speakerLabel={seg.speaker_label}
                  partyId={seg.party_id}
                  parties={parties}
                />
              )}
              <span className="font-mono text-[11px] text-gray-400">
                [{formatTime(seg.start_time)}]
              </span>
              {pickerFor === seg.speaker_label && onRelabel && (
                <span className="flex flex-wrap items-center gap-1">
                  <span className="text-[11px] text-gray-500">is actually:</span>
                  {relabelOptions!
                    .filter((n) => n !== seg.speaker_label)
                    .map((name) => (
                      <button
                        key={name}
                        type="button"
                        className="rounded-full bg-surface-muted px-1.5 py-0.5 text-[11px] text-brand hover:underline"
                        onClick={() => {
                          setPickerFor(null);
                          onRelabel(seg.speaker_label, name);
                        }}
                      >
                        {name}
                      </button>
                    ))}
                  <button
                    type="button"
                    className="text-[11px] text-gray-400"
                    onClick={() => setPickerFor(null)}
                  >
                    ✕
                  </button>
                </span>
              )}
            </div>
            <p className="mt-1 leading-relaxed text-gray-800">{seg.content}</p>
          </li>
        ))}
      </ol>
    </div>
  );
}
